import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { translate, TranslocoService } from '@jsverse/transloco';
import { Subscription, fromEvent } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import {
  Filter,
  FilterService,
  NumericFilterDisplayValue,
} from 'app/modules/collection/services/filter.service';

@Component({
  selector: 'app-numeric-metadata-popover',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './numeric-metadata-popover.component.html',
  styleUrls: ['./numeric-metadata-popover.component.scss'],
})
export class NumericMetadataPopoverComponent implements OnInit, OnDestroy {
  @Input() filter: Filter;
  @Output() valueChange = new EventEmitter<Filter>();
  constructor(
    private formBuilder: UntypedFormBuilder,
    private filterService: FilterService,
    private translocoService: TranslocoService
  ) {}
  numMetaForm: UntypedFormGroup;
  operatorDropdownData: DropdownObject[] = [
    { title: '', id: '<' },
    { title: '', id: '>' },
    { title: '', id: '>=' },
    { title: '', id: '<=' },
    { title: '', id: '=' },
  ];
  numericRangeDropdownData: DropdownObject[] = [];
  isNumericRange: boolean;
  displayValue: NumericFilterDisplayValue;
  translationSubscription: Subscription;
  mousedownSubscription: Subscription;

  ngOnInit() {
    //Hack for closing popover with outside click. Otherwise, selecting dropdowns inside popover will
    //close popover. TODO: Use new Kitchen popover component instead.
    this.mousedownSubscription = fromEvent(document, 'mousedown').subscribe(($event) => {
      let target = $event.target as any;
      while (target) {
        if (
          target.className?.includes('kxi-dropdown') ||
          target.className?.includes('kxi-popout-menu')
        ) {
          return;
        }
        target = target.parentNode;
      }
      this.valueChange.emit(null);
    });

    this.translationSubscription = this.translocoService
      .selectTranslateObject('numeric-metadata-popover')
      .subscribe((o) => {
        this.operatorDropdownData = [
          { id: '>', title: o['greater'] },
          { id: '<', title: o['lesser'] },
          { id: '>=', title: o['greater-equal'] },
          { id: '<=', title: o['lesser-equal'] },
          { id: '=', title: o['equal'] },
        ];
      });
    this.numericRangeDropdownData = [...this.operatorDropdownData];
    this.numericRangeDropdownData.push({
      title: translate('numeric-metadata-popover.numeric-data'),
      id: 'numericRange',
    });
    this.numMetaForm = this.formBuilder.group({
      operator: ['='],
      numericValue: [],
      leftOperator: ['='],
      leftNumericValue: [],
      booleanOperator: ['AND'],
      rightOperator: [],
      rightNumericValue: [],
    });
    this.displayValue = this.fromFilter(this.filter);

    this.displayValue.booleanOperator
      ? (this.isNumericRange = true)
      : (this.isNumericRange = false);

    if (!this.isNumericRange) {
      this.numMetaForm.patchValue({
        operator: this.displayValue.leftOperand.operator,
        numericValue: this.displayValue.leftOperand.value,
      });
    } else {
      this.numMetaForm.patchValue({
        operator: 'numericRange',
        leftOperator: this.displayValue.leftOperand.operator,
        leftNumericValue: this.displayValue.leftOperand.value,
        booleanOperator: this.displayValue.booleanOperator,
        rightOperator: this.displayValue.rightOperand.operator,
        rightNumericValue: this.displayValue.rightOperand.value,
      });
    }
    this.setFormValidity();
  }

  ngOnDestroy(): void {
    this.translationSubscription?.unsubscribe();
    this.mousedownSubscription?.unsubscribe();
  }

  private setFormValidity() {
    for (const key in this.numMetaForm.controls) {
      if (this.numMetaForm.controls.hasOwnProperty(key)) {
        //operator always required
        if (key === 'operator') {
          this.numMetaForm.get(key).setValidators(Validators.required);
          // numericValue only required for non-range
        } else if (key === 'numericValue') {
          this.isNumericRange
            ? this.numMetaForm.get(key).clearValidators()
            : this.numMetaForm.get(key).setValidators(Validators.required);
          //other values only required for range
        } else {
          this.isNumericRange
            ? this.numMetaForm.get(key).setValidators(Validators.required)
            : this.numMetaForm.get(key).clearValidators();
        }
        this.numMetaForm.get(key).updateValueAndValidity();
      }
    }
  }

  fromFilter = (filter: Filter) => {
    return this.filterService.numericRangeFilterToModel(filter.value);
  };

  toFilter = () => {
    this.filter.value = this.filterService.numericRangeModelToFilter(this.displayValue);
    return this.filter;
  };

  get isFormValid() {
    return this.numMetaForm.invalid;
  }

  onValueChange(id: string) {
    id === 'numericRange' ? (this.isNumericRange = true) : (this.isNumericRange = false);
    this.setFormValidity();
  }

  onCancel() {
    this.valueChange.emit(null);
  }

  onSave() {
    const formValue = this.numMetaForm.value;
    this.displayValue = {
      leftOperand: {
        operator: this.isNumericRange ? formValue.leftOperator : formValue.operator,
        value: this.isNumericRange ? formValue.leftNumericValue : formValue.numericValue,
      },
      booleanOperator: this.isNumericRange ? formValue.booleanOperator : null,
      rightOperand: {
        operator: this.isNumericRange ? formValue.rightOperator : null,
        value: this.isNumericRange ? formValue.rightNumericValue : null,
      },
    };
    this.valueChange.emit(this.toFilter());
  }
}
